import { fetchDocsArticle } from "@/lib/docs/load";
import {
	docsArticlePath,
	simpleMarkdownToHtml,
	stripFrontmatter,
} from "@/lib/docs/parse";
import type { DocsManifest } from "@/lib/docs/types";
import { ROUTES } from "@/lib/routes";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useOutletContext, useParams } from "react-router-dom";

export default function DocsArticlePage() {
	const { sectionId = "", slug = "" } = useParams();
	const { manifest } = useOutletContext<{ manifest?: DocsManifest }>();

	const { data, isLoading, error } = useQuery({
		queryKey: ["docs-article", sectionId, slug],
		queryFn: () => fetchDocsArticle(sectionId, slug),
		enabled: Boolean(sectionId && slug),
	});

	if (!sectionId || !slug) {
		return <Navigate to={ROUTES.docs} replace />;
	}

	const section = manifest?.sections.find((s) => s.id === sectionId);
	const page = section?.pages.find((p) => p.slug === slug);

	if (manifest && !page) {
		return <Navigate to={ROUTES.docs} replace />;
	}

	const { meta, body } = data
		? stripFrontmatter(data)
		: { meta: {}, body: "" };
	const html = body ? simpleMarkdownToHtml(body) : "";

	return (
		<article className="docs-prose max-w-3xl">
			<nav className="mb-4 text-xs text-[var(--docs-muted)]">
				<Link to={ROUTES.docs} className="hover:text-[var(--docs-fg)]">
					Docs
				</Link>
				{section ? (
					<>
						{" / "}
						<span>{section.title}</span>
					</>
				) : null}
			</nav>

			{isLoading ? (
				<p className="text-sm text-[var(--docs-muted)]">Loading article…</p>
			) : error ? (
				<div>
					<p className="text-sm text-red-600">Article not found.</p>
					<Link to={ROUTES.docs} className="mt-2 inline-block text-sm underline">
						Back to docs home
					</Link>
				</div>
			) : (
				<>
					<h1 className="font-serif text-3xl font-semibold tracking-tight text-[var(--docs-fg)]">
						{meta.title ?? page?.title ?? slug}
					</h1>
					{meta.description ? (
						<p className="mt-2 text-[15px] text-[var(--docs-muted)]">
							{meta.description}
						</p>
					) : null}
					<div
						className="docs-body mt-6"
						// Content is from trusted protocol docs assets
						dangerouslySetInnerHTML={{ __html: html }}
					/>
					{section && page ? (
						<div className="mt-10 flex flex-wrap gap-2 border-t border-[var(--docs-border)] pt-4 text-xs">
							{section.pages.map((p) => (
								<Link
									key={p.slug}
									to={docsArticlePath(section.id, p.slug)}
									className={
										p.slug === slug
											? "rounded border border-[var(--docs-link)] px-2 py-1 text-[var(--docs-link)]"
											: "rounded border border-[var(--docs-border)] px-2 py-1 text-[var(--docs-muted)] hover:text-[var(--docs-fg)]"
									}
								>
									{p.title}
								</Link>
							))}
						</div>
					) : null}
				</>
			)}
		</article>
	);
}
